import type { LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: Variant | undefined;
  size?: Size | undefined;
  icon?: LucideIcon | undefined;
  iconRight?: LucideIcon | undefined;
  loading?: boolean | undefined;
  children?: ReactNode | undefined;
}

const SIZES: Record<Size, { padding: string; fontSize: number; gap: number; iconSize: number; height: number }> = {
  sm: { padding: '0 10px', fontSize: 11, gap: 5, iconSize: 12, height: 26 },
  md: { padding: '0 12px', fontSize: 12, gap: 6, iconSize: 13, height: 30 },
};

function variantStyle(v: Variant): React.CSSProperties {
  switch (v) {
    case 'primary':
      return {
        background: 'var(--primary)',
        color: 'var(--primary-on)',
        border: '1px solid var(--primary)',
      };
    case 'secondary':
      return {
        background: 'var(--bg-card)',
        color: 'var(--text)',
        border: '1px solid var(--border)',
      };
    case 'ghost':
      return {
        background: 'transparent',
        color: 'var(--text-muted)',
        border: '1px solid transparent',
      };
    case 'danger':
      return {
        background: 'var(--bg-card)',
        color: 'var(--danger)',
        border: '1px solid var(--danger-border)',
      };
  }
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon: Icon,
  iconRight: IconRight,
  loading,
  disabled,
  children,
  style,
  ...rest
}: ButtonProps): JSX.Element {
  const s = SIZES[size];
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: s.gap,
        height: s.height,
        padding: s.padding,
        fontSize: s.fontSize,
        fontWeight: 500,
        borderRadius: 7,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled || loading ? 0.55 : 1,
        transition: 'background 0.12s ease, border-color 0.12s ease',
        whiteSpace: 'nowrap',
        letterSpacing: '-0.005em',
        ...variantStyle(variant),
        ...style,
      }}
    >
      {Icon && <Icon size={s.iconSize} strokeWidth={2.2} />}
      {children}
      {IconRight && <IconRight size={s.iconSize} strokeWidth={2.2} />}
    </button>
  );
}
